import React, { useEffect, useState } from 'react';
import { Chip, CircularProgress, Dialog, DialogContent, DialogTitle, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { knowledgeService, type LessonSourceReference } from '../../../services/knowledgeService';

/** Tranche C3 (#489): sources the generated lessons of a course cite. */
export const CourseSourcesDialog: React.FC<{ courseId: number; open: boolean; onClose: () => void }> = ({ courseId, open, onClose }) => {
    const translate = useTranslate();
    const [lessons, setLessons] = useState<{ lessonId: number; lessonTitle: string; references: LessonSourceReference[] }[] | null>(null);
    const [sourceCount, setSourceCount] = useState(0);

    useEffect(() => {
        if (!open) return;
        setLessons(null);
        knowledgeService.courseReferences(courseId)
            .then((data) => { setLessons(data.lessons); setSourceCount(data.sourceCount); })
            .catch(() => { setLessons([]); setSourceCount(0); });
    }, [open, courseId]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{translate('courseBuilder.sourcesDialog.title')}</DialogTitle>
            <DialogContent>
                {lessons === null && <CircularProgress size={24} />}
                {lessons !== null && lessons.length === 0 && <Typography variant="body2" color="text.secondary">{translate('courseBuilder.sourcesDialog.empty')}</Typography>}
                {lessons !== null && lessons.length > 0 && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('courseBuilder.sourcesDialog.count', { count: sourceCount, lessons: lessons.length })}</Typography>
                        {lessons.map((lesson) => (
                            <div key={lesson.lessonId} data-testid={`sources-lesson-${lesson.lessonId}`}>
                                <Typography variant="subtitle2" sx={{ mt: 1.5 }}>{lesson.lessonTitle}</Typography>
                                <List dense disablePadding>
                                    {lesson.references.map((ref) => (
                                        <ListItem key={ref.id} disableGutters>
                                            <ListItemText
                                                primary={<><Chip size="small" label={ref.refLabel} sx={{ mr: 1 }} />{ref.citation}{!ref.sourceIsCurrent && <Chip size="small" color="warning" sx={{ ml: 1 }} label={translate('courseBuilder.sourcesDialog.retired')} />}</>}
                                                secondary={ref.usageNote ?? undefined}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </div>
                        ))}
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CourseSourcesDialog;
